import React, { useCallback, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Maximize2Icon } from 'lucide-react';
import { SectionTag } from './SectionTag';
import { CTAButton } from './CTAButton';
import { CarouselControls } from './CarouselControls';
import { RecipeViewer } from './RecipeViewer';
import { RECIPE_SAMPLES, PRICE } from './salesData';
import { useCarousel } from '../hooks/useCarousel';
export function InteriorSection() {
  const {
    trackRef,
    index,
    goTo
  } = useCarousel(RECIPE_SAMPLES.length);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const close = useCallback(() => setOpenIdx(null), []);
  return <section id="interior" className="overflow-hidden bg-white py-12 sm:py-16" data-id="element-141" data-mp="3rra2s1j1r5h2">
      <div className="mx-auto max-w-5xl px-5" data-id="element-142" data-mp="3vrsw600l8jld">
        <div className="text-center" data-id="element-143" data-mp="ndbz9og0dvtm">
          <SectionTag className="bg-cream" data-id="element-144" data-mp="nc96faz4ljb2">Por dentro</SectionTag>
          <h2 className="mx-auto mt-3.5 max-w-[24ch] font-display text-[1.5rem] font-extrabold leading-[1.15] tracking-tight text-charcoal sm:max-w-[32ch] sm:text-[2.2rem]" data-id="element-145" data-mp="s3sb5446l9dv">
            Mirá las recetas <span className="mark-yellow" data-id="element-146" data-mp="2s6oe41p8vi10">por dentro</span>
          </h2>
          <p className="mx-auto mt-4 max-w-[46ch] font-sans text-[0.92rem] leading-relaxed text-taupe sm:max-w-[56ch] sm:text-[1rem]" data-id="element-147" data-mp="2s8hi198h3o5b">
            Explorá 3 recetas completas de la colección antes de comprar.
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-3xl" data-id="element-148" data-mp="3fvz5wtoma230">
          <div ref={trackRef} role="region" aria-roledescription="carrusel" aria-label="3 recetas de muestra" className="no-scrollbar relative flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain" data-id="element-149" data-mp="3fuo24tv68ubw">
            {RECIPE_SAMPLES.map((sample, i) => <article key={sample.name} role="group" aria-roledescription="diapositiva" aria-label={`Receta ${i + 1} de ${RECIPE_SAMPLES.length}: ${sample.name}`} className="w-full shrink-0 snap-center px-0.5" data-id="element-150" data-mp="1vgdzqg7qswej" data-mpi={i}>
                <button type="button" onClick={() => setOpenIdx(i)} aria-label={`Ampliar receta: ${sample.name}`} className="group flex h-[380px] w-full items-center justify-center rounded-3xl bg-cream p-3 ring-1 ring-stone focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/60 md:h-[520px] md:p-5" data-id="element-151" data-mp="3vin6zw3amkts">
                  <img src={sample.image} alt={`Página de la receta ${sample.name}`} loading="lazy" decoding="async" width={sample.width} height={sample.height} className="h-full w-auto max-w-full rounded-md bg-white object-contain shadow-[0_18px_40px_-22px_rgba(25,23,19,0.45)] transition-transform duration-200 ease-out group-hover:scale-[1.01]" data-id="element-152" data-mp="ibvayjvssfaj" />
                </button>

                <div className="mt-3.5 flex flex-col items-center gap-2.5 text-center sm:flex-row sm:justify-between sm:text-left" data-id="element-153" data-mp="1b7kxlerny728">
                  <div className="min-w-0" data-id="element-154" data-mp="169jif4xcyrw1">
                    <p className="font-display text-[0.6rem] font-bold uppercase tracking-editorial text-gold-deep" data-id="element-155" data-mp="117u51imny24s">
                      {sample.tomo}
                    </p>
                    <h3 className="mt-0.5 font-display text-[0.98rem] font-bold leading-tight tracking-tight text-charcoal" data-id="element-156" data-mp="1tx1cj743i226">
                      {sample.name}
                    </h3>
                  </div>
                  <button type="button" onClick={() => setOpenIdx(i)} className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl bg-charcoal px-4 font-display text-[0.78rem] font-bold uppercase tracking-[0.04em] text-white transition-colors duration-150 ease-out hover:bg-charcoal-soft focus:outline-none focus-visible:ring-4 focus-visible:ring-charcoal/30" data-id="element-157" data-mp="1k6zrko71gzrh">
                    <Maximize2Icon className="h-4 w-4" aria-hidden strokeWidth={2.5} data-id="element-158" data-mp="1sdhlcyktewie" />
                    Ampliar receta
                  </button>
                </div>
              </article>)}
          </div>

          <CarouselControls label="Receta" index={index} count={RECIPE_SAMPLES.length} onGo={goTo} className="mx-auto mt-5 max-w-sm" data-id="element-159" data-mp="1jgh3las93wnx" />
        </div>

        <p className="mx-auto mt-8 max-w-[46ch] text-center font-sans text-[0.92rem] font-medium leading-relaxed text-charcoal sm:text-[1rem]" data-id="element-160" data-mp="2g43smnmzwcrc">
          Estas son 3 recetas de la colección. Recibí las más de 500 recetas, los 6 tomos y los 4
          bonos por <strong className="font-bold" data-id="element-161" data-mp="26d0qdiibxrqh">AR{PRICE.current}</strong>.
        </p>

        <div className="mt-5 flex justify-center" data-id="element-162" data-mp="2u33fpquv0jle">
          <CTAButton className="w-full max-w-[26rem] px-4" data-id="element-163" data-mp="2yuuyvgborn1i" />
        </div>
      </div>

      <AnimatePresence data-id="element-164" data-mp="2ywza6jbwzobt">
        {openIdx !== null && <RecipeViewer sample={RECIPE_SAMPLES[openIdx]} onClose={close} data-id="element-165" data-mp="nf4717x5bqkz" />}
      </AnimatePresence>
    </section>;
}